import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { X } from 'react-feather';

export const HelperProfileModal = ({ isOpen, onClose, helperId }) => {
  const { profile: viewerProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && helperId) {
      setLoading(true);
      const fetchData = async () => {
        try {
          // 1. Get Profile
          const userSnap = await getDoc(doc(db, 'users', helperId));
          if (userSnap.exists()) setProfile(userSnap.data());

          let foundReviews = [];

          // 2. Fetch strictly from 'reviews' collection
          const allReviewsSnap = await getDocs(collection(db, 'reviews'));
          
          allReviewsSnap.forEach(docSnap => {
            const data = docSnap.data();
            
            // STRICT MATCH: Only include if the review is FOR this helper.
            // Check 'reviewedUserId' (standard) or 'tutorId' (alternative).
            // DO NOT match 'reviewerId' (which would be reviews BY John).
            const isReviewOfHelper = 
              data.reviewedUserId === helperId || 
              data.tutorId === helperId;
            
            if (isReviewOfHelper) {
              // 3. Get comment from 'comment' field (as seen in Firestore screenshot)
              const commentText = data.comment || 
                                  data.reviewComment || 
                                  data.message || 
                                  data.feedback || 
                                  data.text || 
                                  '';

              foundReviews.push({ 
                id: docSnap.id, 
                reviewerName: data.reviewerName || data.requesterName || 'User',
                rating: data.rating || 0,
                comment: commentText || 'No comment', 
                date: data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : new Date()
              });
            }
          });

          // Sort by date (newest first)
          foundReviews.sort((a, b) => b.date - a.date);
          setReviews(foundReviews);

        } catch (err) {
          console.error('Error fetching profile:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [isOpen, helperId]);

  if (!isOpen || !helperId) return null;

  const isTeacher = viewerProfile?.isTeacher === true;
  const subjectsList = profile?.subjectsGoodAt ? (Array.isArray(profile.subjectsGoodAt) ? profile.subjectsGoodAt : [profile.subjectsGoodAt]) : [];

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 3000, background: 'rgba(0,0,0,0.5)' }}>
      <div className="helper-profile-modal" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '2rem', maxWidth: '500px', width: '90%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
        
        {loading ? <p style={{textAlign:'center', padding:'2rem'}}>Loading...</p> : profile && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#800000', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '2rem' }}>
                {profile.displayName?.charAt(0) || 'U'}
              </div>
              <h3 style={{ margin: 0, fontSize: '1.5rem' }}>{profile.displayName}</h3>
              {profile.grade && <p style={{ color: '#666', margin: '0.5rem 0 0' }}>Grade {profile.grade}</p>}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-around', background: '#f9fafb', padding: '1rem', borderRadius: '12px', marginBottom: '2rem' }}>
              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '1.2rem', color:'#800000' }}>{profile.points || 0}</div><div style={{ fontSize: '0.8rem', color: '#666' }}>Points</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '1.2rem', color:'#800000' }}>{profile.rating?.toFixed(1) || '0.0'}</div><div style={{ fontSize: '0.8rem', color: '#666' }}>Rating</div></div>
              <div style={{ textAlign: 'center' }}><div style={{ fontWeight: 'bold', fontSize: '1.2rem', color:'#800000' }}>{profile.ratingCount || 0}</div><div style={{ fontSize: '0.8rem', color: '#666' }}>Sessions</div></div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ margin: '0 0 0.5rem', color: '#800000' }}>Subjects I'm Good At</h4>
              {subjectsList.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {subjectsList.map((s, i) => <span key={i} style={{ background: '#f0fdf4', color: '#166534', padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.9rem' }}>{s}</span>)}
                </div>
              ) : <p style={{ color: '#999', fontStyle: 'italic' }}>Not specified</p>}
            </div>

            <div>
              <h4 style={{ margin: '0 0 1rem', color: '#800000' }}>Reviews ({reviews.length})</h4>
              {reviews.length > 0 ? reviews.map((r, i) => {
                const displayName = isTeacher ? (r.reviewerName || 'Unknown') : 'Student User';
                return (
                  <div key={i} style={{ background: '#fff', border: '1px solid #eee', padding: '1rem', borderRadius: '8px', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600 }}>{displayName}</span>
                      <span style={{ color: '#f59e0b' }}>
                         {'★'.repeat(Math.round(r.rating || 0))}{'☆'.repeat(5 - Math.round(r.rating || 0))}
                      </span>
                    </div>
                    <p style={{ margin: 0, color: '#555', fontStyle: 'italic' }}>"{r.comment}"</p>
                  </div>
                );
              }) : <p style={{ color: '#999', fontStyle: 'italic' }}>No reviews yet.</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
